import React, { useState, useEffect } from 'react';
import { Upload, X, Link as LinkIcon, File } from 'lucide-react';
import { Button } from '../ui/button';
import { getFileUrl } from '../../lib/utils';

const FileUpload = ({ value, onChange, label, accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" }) => {
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [fileUrl, setFileUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    setFileUrl(value || '');
  }, [value]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('حجم الملف يجب أن يكون أقل من 10MB');
      return;
    }

    setUploading(true);
    setFileName(file.name);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/upload/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const data = await response.json();
      // Use the full URL if available, otherwise use file_path with BACKEND_URL
      const uploadedUrl = data.url || `${BACKEND_URL}${data.file_path}`;
      setFileUrl(uploadedUrl);
      onChange(uploadedUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('فشل رفع الملف');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (url) => {
    setFileUrl(url);
    onChange(url);
  };

  const handleRemove = () => {
    setFileUrl('');
    setFileName('');
    onChange('');
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Upload Mode Selector */}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'url' ? 'default' : 'outline'}
          onClick={() => setUploadMode('url')}
          className="flex-1"
        >
          <LinkIcon className="w-4 h-4 ml-2" />
          رابط خارجي
        </Button>
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'file' ? 'default' : 'outline'}
          onClick={() => setUploadMode('file')}
          className="flex-1"
        >
          <Upload className="w-4 h-4 ml-2" />
          رفع ملف
        </Button>
      </div>

      {/* URL Input Mode */}
      {uploadMode === 'url' && (
        <input
          type="url"
          value={fileUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://example.com/file.pdf"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] focus:border-transparent"
        />
      )}

      {/* File Upload Mode */}
      {uploadMode === 'file' && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0F388E] transition-colors">
          <input
            type="file"
            accept={accept}
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            disabled={uploading}
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {uploading ? 'جاري الرفع...' : 'اضغط لرفع ملف'}
            </span>
            <span className="text-xs text-gray-400 mt-1">الحد الأقصى: 10MB</span>
          </label>
        </div>
      )}

      {/* File Preview */}
      {fileUrl && (
        <div className="relative bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <File className="w-8 h-8 text-[#0F388E]" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {fileName || 'ملف مرفوع'}
              </p>
              <a
                href={getFileUrl(fileUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#0F388E] hover:underline"
              >
                معاينة الملف
              </a>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
